import { type FC, useCallback, useMemo } from "react";
import { useFormikContext } from "formik";

import { useCurrentUserRolePermission } from "@vega-api/extended/auth/permissions/useCurrentUserRolePermission";
import { TabNavigation } from "@vega-core/components/Layout/TabNavigation";
import { m } from "@vega-core/paraglide/messages";
import { isFalsy } from "@vega-core/utils/isFalsy";

import { useRoleTabsData } from "../../hooks/useRoleTabsData";

import { RolesTab } from "./RolesTab";
import { RolesTabSkeleton } from "./RolesTabSkeleton";

export const StepRoles: FC = () => {
  const { setFieldValue } = useFormikContext<{
    roles: Record<string, { roleId: string }>;
  }>();

  const { currentUserPermissions, isLoading } = useCurrentUserRolePermission();
  const tabData = useRoleTabsData(currentUserPermissions);

  const handleRoleChange = useCallback(
    (role: RoleNew, subjectId: string, subjectType: SubjectType) => {
      if (isFalsy(role.id)) {
        // Clear the state for the given subjectId
        setFieldValue(`roles.${subjectId}`, undefined);
      } else {
        setFieldValue(`roles.${subjectId}`, {
          roleId: role.id,
          subjectId,
          subjectType: subjectType,
        });
      }
    },
    [setFieldValue],
  );

  const tabs = useMemo(() => {
    const tempTabs = [];

    tempTabs.push({
      title: m.userManagement_permissions_offerings(),
      index: SubjectType.OPPORTUNITY,
      component: isLoading ? (
        <RolesTabSkeleton />
      ) : (
        <RolesTab
          onRoleChange={(role, subjectId) =>
            handleRoleChange(role, subjectId, SubjectType.OPPORTUNITY)
          }
          permissions={tabData[SubjectType.OPPORTUNITY] || []}
          roleContext={RoleContext.PRE_INVESTMENT}
        />
      ),
    });

    if (buildTimeFlags.reporting_settings_investments) {
      tempTabs.push({
        title: m.userManagement_permissions_investments(),
        index: SubjectType.INVESTMENT,
        component: isLoading ? (
          <RolesTabSkeleton />
        ) : (
          <RolesTab
            onRoleChange={(role, subjectId) =>
              handleRoleChange(role, subjectId, SubjectType.INVESTMENT)
            }
            permissions={tabData[SubjectType.INVESTMENT] || []}
            roleContext={RoleContext.POST_INVESTMENT}
          />
        ),
      });
    }

    return tempTabs;
  }, [isLoading, tabData, handleRoleChange]);

  return (
    <div className="flex flex-col gap-6 mb-6">
      <div className="body-base-regular text-text/secondary">
        <p>{m.userManagement_permissions_description()}</p>
      </div>

      <TabNavigation innerGap={false} tabs={tabs} transparentBG />
    </div>
  );
};

import { RoleContext } from "@vega-api/generated/types/roleContext";
import { type RoleNew } from "@vega-api/generated/types/roleNew";
import { SubjectType } from "@vega-api/generated/types/subjectType";
import { buildTimeFlags } from "@vega-core/utils/buildTimeFlags";
